/**
 * FormTextarea — textarea avec liaison React Hook Form automatique.
 *
 *   <FormTextarea name="description_fr" label="Description" rows={4} />
 *
 * Utilise `useFormContext()`. Voir FormField pour les contraintes.
 */
import { useFormContext, type FieldPath, type FieldValues } from 'react-hook-form'

import Field from '@/components/ui/Field'
import Textarea from '@/components/ui/Textarea'
import { getNestedError } from '@/utils/formErrors'

interface FormTextareaProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  label: string
  required?: boolean
  helper?: string
  disabled?: boolean
  placeholder?: string
  rows?: number
  hideLabel?: boolean
  maxLength?: number
}

export function FormTextarea<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  required,
  helper,
  disabled,
  placeholder,
  rows = 3,
  hideLabel,
  maxLength,
}: FormTextareaProps<TFieldValues>) {
  const {
    register,
    formState: { errors },
  } = useFormContext<TFieldValues>()

  const errorMessage = getNestedError(errors as Record<string, unknown>, name)

  return (
    <Field
      label={label}
      required={required}
      helper={helper}
      error={errorMessage}
      hideLabel={hideLabel}
    >
      {(controlProps) => (
        <Textarea
          {...controlProps}
          {...register(name)}
          rows={rows}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
        />
      )}
    </Field>
  )
}

export default FormTextarea
